import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCart,
  updateCartItem,
  removeFromCart,
  clearCart,
} from "../Redux/Slice/cart.slice";
import { IMAGE_URL } from "../Utils/baseUrl";
import { useNavigate } from "react-router-dom";
import BackgroundColor from "../component/BackgroundColor";
import { FiTrash2 } from "react-icons/fi";
import axios from "axios"; // If not already imported
import axiosInstance from "../Utils/axiosInstance";
import { generateInvoice } from "../Utils/invoiceGenerator";
import { createOrder, verifyPayment } from "../Redux/Slice/Payment.slice";
import { decryptData } from "../Utils/encryption";
import CartSkeleton from "../component/skeleton-loaders/CartSkeleton";

export default function Cart() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector((state) => state.cart.items);
  const cartLoading = useSelector((state) => state.cart.loading);
  const user = useSelector((state) => state.auth.user)
  const userId = localStorage.getItem("yoyouserId");
  const token = localStorage.getItem("yoyoToken");
  const { order, razorpayOrder, paymentStatus, loading, error } = useSelector((state) => state.payment);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    document.title = 'Cart • YOYO';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!(userId && token)) {
      navigate("/login");
      return;
    }
    if (userId) {
      dispatch(fetchCart());
    }
  }, [dispatch, userId, token, navigate]);

  const handleCheckout = async () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true)
    const items = (Array.isArray(cartItems) ? cartItems.map(it => ({
      game: it.game?._id || it.game,
      name: it.name,
      platform: it.platform,
      price: Number(it.price || it?.game?.platforms?.[it.platform]?.price || 0),
    })) : []);

    // Calculate totals
    const subtotal = items.reduce((sum, it) => sum + it.price, 0);
    const tax = subtotal * 0.18;
    const discount = subtotal * 0.05;
    const totalAmount = subtotal + tax - discount;

    const resultAction = await dispatch(createOrder({ amount: totalAmount }));
    if (createOrder.fulfilled.match(resultAction)) {
      // setCheckoutLoading(false)
      const { razorpayOrder } = resultAction.payload;

      const options = {
        key: "rzp_test_hN631gyZ1XbXvp",
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: "Yoyo",
        description: "Game Purchase",
        order_id: razorpayOrder.id,
        image: "/logo556.png",
        handler: function (response) {
          dispatch(verifyPayment({
            items,
            amount: totalAmount,
            paymentResponse: response,
            buyNow: false
          })).then((res) => {
            if (verifyPayment.fulfilled.match(res)) {
              try {
                const updatedOrder = res.payload.order;
                const userDetails = {
                  name: user?.fullName ? decryptData(user.fullName) : "Customer",
                  email: user?.email ? decryptData(user.email) : "",
                };
                generateInvoice(updatedOrder, userDetails, 'open');
              } catch (e) {
                console.error("Error auto-opening invoice from cart", e);
              }
            }
          });
        },
        prefill: {
          name: decryptData(user.fullName),
          email: decryptData(user.email),
        },
        theme: {
          color: "#7c3aed",
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
      setCheckoutLoading(false);
    }
  };

  const shouldShowSkeleton = cartLoading || (userId && token && (!Array.isArray(cartItems) || typeof cartItems === 'undefined'));

  if (shouldShowSkeleton) {
    return (
      <BackgroundColor className="relative overflow-x-hidden">
        <CartSkeleton />
      </BackgroundColor>
    );
  }

  return (
    <BackgroundColor className="relative overflow-x-hidden">
      <div className="pt-20 sm:pt-24 pb-12 sm:pb-16 w-full max-w-[1280px] mx-auto px-3 sm:px-4">
        <div className="relative mb-6 sm:mb-8">
          <h1 className="text-[20px] sm:text-[24px] md:text-[36px] lg:text-[44px] font-extrabold tracking-wide text-white">
            Your Cart
          </h1>
          <p className="text-white/60 mt-1 text-sm sm:text-base">
            Review your games and proceed to checkout
          </p>
        </div>

        {!cartItems || cartItems.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-6 sm:p-10 text-center flex flex-col items-center justify-center min-h-[400px] sm:min-h-[500px]">
            <div className="text-xl sm:text-2xl md:text-3xl font-semibold text-white mb-2">
              Your cart is empty
            </div>
            <div className="text-white/60 mb-6 text-sm sm:text-base">
              Explore our collection and add some games to your cart.
            </div>
            <button
              onClick={() => navigate("/games")}
              className="px-4 sm:px-6 py-2 sm:py-3 rounded-md bg-gradient-primary transition-all text-sm sm:text-base"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Items */}
            <div className="lg:col-span-2 space-y-3 sm:space-y-4 min-w-0">
              {cartItems.map((it) => {
                const gameId = it.game?._id || it.game;
                const platformPrice = Number(
                  it?.game?.platforms?.[it.platform]?.price || 0
                );
                const unitPrice = Number(it.price || platformPrice || 0);
                return (
                  <div
                    key={`${gameId}:${it.platform}`}
                    className="group bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-5 flex gap-3 sm:gap-4 hover:border-purple-400/40 transition-colors w-full overflow-hidden"
                  >
                    <div className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 overflow-hidden rounded-lg sm:rounded-xl shrink-0">
                      <img
                        src={
                          it.game?.cover_image?.url ||
                          `${IMAGE_URL}placeholder.png`
                        }
                        alt={it.game?.title || "Game"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 sm:gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-white font-semibold truncate text-sm sm:text-base md:text-lg">
                            {it.game?.title || "Untitled"}
                          </div>
                          <div className="mt-1 flex items-center gap-1.5 sm:gap-2">
                            <span className="px-1.5 sm:px-2 py-0.5 rounded-full text-xs bg-white/10 text-white/80 border border-white/10">
                              {it.platform}
                            </span>
                            <span className="text-white/60 text-xs">
                              ${unitPrice.toFixed(2)} each
                            </span>
                          </div>
                        </div>
                        <div className="shrink-0">
                          <button
                            onClick={() =>
                              dispatch(
                                removeFromCart({ gameId, platform: it.platform })
                              )
                            }
                            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg bg-white/5 hover:bg-red-500/20 border border-white/10 hover:border-red-500/40 text-white/80 hover:text-red-400 transition-colors"
                            aria-label="Remove from cart"
                          >
                            <FiTrash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className="h-fit lg:sticky lg:top-24">
              {(() => {
                const subtotal = Array.isArray(cartItems)
                  ? cartItems.reduce(
                    (s, it) =>
                      s +
                      Number(
                        it.price ||
                        it?.game?.platforms?.[it.platform]?.price ||
                        0
                      ),
                    0
                  )
                  : 0;
                const tax = subtotal * 0.18;
                const discount = subtotal * 0.05;
                const total = subtotal + tax - discount;

                return (
                  <div className="bg-white/5 border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-5">
                    <div className="flex items-center justify-between text-white/80 mb-2">
                      <span className="text-sm sm:text-base">Subtotal</span>
                      <span className="text-white font-semibold text-sm sm:text-base">
                        ${subtotal.toFixed(2)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-white/60 text-xs sm:text-sm mb-2">
                      <span>Tax (18%)</span>
                      <span>+${tax.toFixed(2)}</span>
                    </div>

                    <div className="flex items-center justify-between text-white/60 text-xs sm:text-sm mb-4">
                      <span>Discount (5%)</span>
                      <span className="text-green-500">-${discount.toFixed(2)}</span>
                    </div>

                    <div className="border-t border-white/10 my-4"></div>

                    <div className="flex items-center justify-between text-white font-bold text-base sm:text-lg mb-6">
                      <span>Total</span>
                      <span>${total.toFixed(2)}</span>
                    </div>

                    <div className="md:flex lg:flex-col items-center justify-between">
                      <button
                        className="w-full py-2 lg:py-3 rounded-md bg-gradient-primary text-white font-semibold shadow-lg transition-all"
                        onClick={handleCheckout}
                        disabled={checkoutLoading}
                      >
                        {checkoutLoading ? "Processing..." : "Proceed to Checkout"}
                      </button>
                      <button
                        onClick={() => dispatch(clearCart())}
                        className="w-full py-2 xs:mt-3 md:mt-0 lg:mt-3 md:mx-3 rounded-md bg-white/10 hover:bg-white/20 text-white transition-colors"
                      >
                        Clear Cart
                      </button>
                      <button
                        onClick={() => navigate("/games")}
                        className="w-full py-2 xs:mt-2 md:mt-0 lg:mt-2 rounded-md bg-white/5 hover:bg-white/10 text-white/80 hover:text-white transition-colors"
                      >
                        Continue Shopping
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </BackgroundColor>
  );
}
